import React, { useState, useEffect, useMemo } from "react";
import { COLORS } from "../../theme/colors";
import { MATERIALS } from "../../theme/materials";
import { templateApi } from "../../api/client";
import IconTile from "../../components/IconTile";
import Icon from "../../components/Icon";
import { Button, TogglePill } from "../../components/ui";
import {
  DEFAULT_CATEGORIES, categoryLabel, resolveTemplateIcon,
} from "./iphone-grid/iconForTemplate";
import { resolveAgentIcon } from "../library/iphone-grid/iconForAgent";

// agent-experience-redesign-v1.md §20.3 / M8 — PublishTemplateModal
// rewrite. Modal chrome aligned with InstallTemplateModal /
// BindIntegrationModal. NEW: "+ New category" affordance per
// §20.12 Q2 — user can type a custom category label that joins
// the chip strip on next refresh of MarketplaceSearchBar.
//
// Categories shipped: 12 domain defaults + a "+ Other…" tile that
// reveals a small inline form for the custom label. The custom
// label is sent verbatim as template.category — buildCategoryStrip
// will surface it in the chip strip the moment it's published.

const PublishTemplateModal = ({ agent, onClose, onPublished }) => {
  const [name, setName] = useState(agent?.name || "");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("generic");
  const [customCategoryMode, setCustomCategoryMode] = useState(false);
  const [customCategory, setCustomCategory] = useState("");
  const [tags, setTags] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);

  // Esc closes.
  useEffect(() => {
    if (!agent) return;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [agent, onClose]);

  // Preview tile uses iconForTemplate so the user sees what the
  // marketplace tile will look like once published.
  const previewIcon = useMemo(() => {
    const effectiveCategory = customCategoryMode ? slugify(customCategory) : category;
    return resolveTemplateIcon({ category: effectiveCategory, template: agent?.template });
  }, [customCategoryMode, customCategory, category, agent]);

  // Fall back to the agent's IconTile derivation when category-derived
  // resolution lands on the generic fallback.
  const agentIcon = useMemo(() => agent ? resolveAgentIcon(agent) : null, [agent]);

  if (!agent) return null;

  const tileCategory = previewIcon.category || (agentIcon && agentIcon.category) || "marketplace";
  const tileIcon     = previewIcon.iconName || (agentIcon && agentIcon.iconName) || "tab.marketplace";

  const handlePublish = async () => {
    if (!name.trim()) { setError("Name is required"); return; }
    let finalCategory = category;
    if (customCategoryMode) {
      if (!customCategory.trim()) {
        setError("Custom category label is required");
        return;
      }
      finalCategory = slugify(customCategory);
    }
    setPublishing(true);
    setError(null);
    try {
      const tagList = tags.split(",").map((t) => t.trim()).filter(Boolean);
      const res = await templateApi.publishFromAgent(agent.id, {
        name, description, category: finalCategory, tags: tagList, visibility,
        agent_snapshot: {},
      });
      if (res.success) {
        onPublished?.(res.data);
        onClose();
      } else {
        setError(res.error || "Publish failed");
      }
    } catch (e) {
      // Surface structured 409 detail when present (e.g.
      // template_name_conflict from the publish endpoint) so the user
      // sees a clear "rename or bump the version" message instead of
      // the stringified detail dict the wrapper falls back to.
      const detail = e?.body?.detail;
      const friendly = (typeof detail === "object" && detail?.message)
        ? detail.message
        : (e.message || String(e));
      setError(friendly);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        ...MATERIALS.thickGlass(),
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12, width: "92%", maxWidth: 580, maxHeight: "92vh",
        overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: MATERIALS?.spatialShadow ? MATERIALS.spatialShadow() : "0 16px 40px rgba(0,0,0,0.5)",
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
            <IconTile category={tileCategory} iconName={tileIcon} size={40} />
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 14, fontWeight: 700, color: COLORS.text,
                fontFamily: "Outfit, sans-serif",
              }}>Publish to marketplace</div>
              <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 3 }}>
                Share <strong>{agent.name}</strong> for others to install
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} title="Close (Esc)" style={{
            background: "transparent", border: "none", color: COLORS.textDim,
            cursor: "pointer", padding: 4,
          }}><Icon name="action.close" size={14} color={COLORS.textDim} /></button>
        </div>

        {/* Body */}
        <div className="panel-scroll" style={{ flex: 1, overflow: "auto", padding: 20 }}>
          {/* Agent summary */}
          <div style={{
            padding: "10px 14px", borderRadius: 8, background: COLORS.surface,
            border: `1px solid ${COLORS.border}`, marginBottom: 16,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <Icon name="block.bot" size={20} color={COLORS.text} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{agent.name}</div>
              <div style={{ fontSize: 11, color: COLORS.textDim }}>
                {agent.model || "(no model)"} · Temp {agent.temperature ?? "?"}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field
              label="Template name"
              value={name}
              onChange={setName}
              required
            />

            <Field
              label="Description"
              value={description}
              onChange={setDescription}
              placeholder="What does this template do well?"
              rows={3}
            />

            {/* Category — 12 domain defaults + custom-category mode toggle */}
            <div>
              <label style={labelStyle}>Category<span style={{ color: COLORS.pink || "#ef4444" }}> *</span></label>
              {!customCategoryMode ? (
                <>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {DEFAULT_CATEGORIES.map((c) => (
                      <TogglePill
                        key={c}
                        active={category === c}
                        size="sm"
                        onClick={() => setCategory(c)}
                      >{categoryLabel(c)}</TogglePill>
                    ))}
                    <TogglePill
                      size="sm"
                      onClick={() => { setCustomCategoryMode(true); setCustomCategory(""); }}
                      style={{
                        borderStyle: "dashed",
                        color: COLORS.cyan,
                        borderColor: `${COLORS.cyan}66`,
                      }}
                    >+ New category</TogglePill>
                  </div>
                </>
              ) : (
                <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
                  <input
                    type="text"
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    placeholder="e.g. Billing disputes"
                    autoFocus
                    style={{ ...fieldStyle, flex: 1 }}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => { setCustomCategoryMode(false); setCustomCategory(""); }}
                  >Cancel</Button>
                </div>
              )}
              <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
                {customCategoryMode
                  ? `Will publish as "${slugify(customCategory) || "(empty)"}". Appears in the chip strip after refresh.`
                  : "Pick from 12 standard domains or add a custom category."}
              </div>
            </div>

            <Field
              label="Tags (comma-separated)"
              value={tags}
              onChange={setTags}
              placeholder="support, faq, escalation"
            />

            {/* Visibility */}
            <div>
              <label style={labelStyle}>Visibility</label>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { value: "public",  label: "Public",  desc: "Visible to all tenants" },
                  { value: "tenant",  label: "Tenant",  desc: "Visible only to your tenant" },
                  { value: "private", label: "Private", desc: "Hidden; install via direct link only" },
                ].map((v) => {
                  const active = visibility === v.value;
                  return (
                    <button
                      key={v.value}
                      type="button"
                      onClick={() => setVisibility(v.value)}
                      title={v.desc}
                      style={{
                        flex: 1,
                        padding: "10px 12px", fontSize: 11, fontWeight: 600,
                        borderRadius: 12, cursor: "pointer",
                        background: active ? `${COLORS.accent}15` : "transparent",
                        border: `1px solid ${active ? `${COLORS.accent}55` : COLORS.border}`,
                        color: active ? COLORS.text : COLORS.textMuted,
                        display: "flex", flexDirection: "column", alignItems: "flex-start",
                        gap: 2, transition: "all 0.15s",
                      }}
                    >
                      <span>{v.label}</span>
                      <span style={{ fontSize: 9, color: active ? COLORS.textDim : COLORS.textDim, fontWeight: 400 }}>{v.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {error && (
            <div style={{
              marginTop: 14, padding: "8px 12px", borderRadius: 6,
              background: `${COLORS.pink || "#ef4444"}15`,
              border: `1px solid ${COLORS.pink || "#ef4444"}55`,
              color: COLORS.pink || "#ef4444", fontSize: 12,
            }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 20px", borderTop: `1px solid ${COLORS.border}`,
          display: "flex", justifyContent: "flex-end", gap: 8,
        }}>
          <Button variant="secondary" onClick={onClose} disabled={publishing}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handlePublish}
            disabled={publishing || !name.trim()}
            loading={publishing}
            style={{
              boxShadow: `0 2px 10px ${COLORS.accent}30`,
            }}
          >{publishing ? "Publishing…" : "Publish"}</Button>
        </div>
      </div>
    </div>
  );
};

function Field({ label, value, onChange, placeholder, required, rows }) {
  return (
    <div>
      <label style={labelStyle}>
        {label}{required && <span style={{ color: COLORS.pink || "#ef4444" }}> *</span>}
      </label>
      {rows ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          style={{ ...fieldStyle, resize: "vertical" }}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={fieldStyle}
        />
      )}
    </div>
  );
}

function chipStyle(active) {
  return {
    padding: "5px 12px", fontSize: 11, fontWeight: 600,
    borderRadius: 999, cursor: "pointer",
    background: active ? `${COLORS.accent}18` : "transparent",
    border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
    color: active ? COLORS.accent : COLORS.textMuted,
    transition: "all 0.15s",
    whiteSpace: "nowrap",
  };
}

// Exported for unit testing
export function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 600,
  color: COLORS.textMuted, marginBottom: 4, letterSpacing: "0.02em",
};

const fieldStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "8px 12px", fontSize: 12, color: COLORS.text,
  background: COLORS.surface,
  border: `1px solid ${COLORS.border}`, borderRadius: 6,
  outline: "none", fontFamily: "inherit",
};

export default PublishTemplateModal;
